import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { BlacklistTemplate } from '@core/models/blacklist-template.model';
import { Status } from '@core/enums/status.enum';
import { AppPermissionService } from '@core/services/app-permission.service';
import { BlacklistTemplateService } from '../../services/blacklist-template.service';

type RowsByCurrency = Record<string, any[]>;

@Component({
  selector: 'kt-blacklist-template-setting',
  templateUrl: './blacklist-template-setting.component.html',
  styleUrls: ['./blacklist-template-setting.component.scss'],
})
export class BlacklistTemplateSettingComponent implements OnInit, OnDestroy {
  form!: FormGroup;

  dropdown = {
    statuses: this.dropdownHttpService.statuses,
    currencies: [] as any[],
  };

  status = Status;

  saving = false;
  dataLoading = true;
  formReady = false;

  selectedIndex = 0;
  selectedCurrency!: { id: number; name: string };

  providersByCurrency: any[][] = [];
  unFilterProvidersByCurrency: any[][] = [];
  selectedGameProvider: any[] = [];

  filteredFormWithIndex: Array<{ idx: number; group: FormGroup }> = [];

  keyword = '';

  // Apply-to-other-currencies
  showCurrencyDialog = false;
  selectedAppliedCurrencies: number[] = [];
  beforeApplied = true;
  afterApplied = false;

  titleAction?: string;

  // map[currencyId][providerId][categoryCode] => FormGroup
  private categoryMap: Record<number, Record<number, Record<string, FormGroup>>> = {};

  private sub = new Subscription();

  public messages$ = this.blacklistTemplateService.messages$;
  public icon$ = this.blacklistTemplateService.icon$;
  lastIcon: 'success' | 'error' | 'info' | 'warning' | 'question' = 'info';

  templateDropdownSettings = {
    singleSelection: true,
    text: 'Please select',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    classes: 'dropdown',
    primaryKey: 'id',
    labelKey: 'name',
    noDataLabel: '',
    showCheckbox: false,
  };
  templateDropdownList: BlacklistTemplate[] = [];
  templateSelectedItems: any[] = [];
  selectedBlacklistId: number = 0;
  templateLocked = false;
  canEditBlacklist = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      mode: string;
      template: BlacklistTemplate;
      setting: RowsByCurrency;
      selected_blacklist_id?: number;
      selected_sub_categories?: Record<number, Record<number, number[]>>;
    },
    public dialogRef: MatDialogRef<BlacklistTemplateSettingComponent>,
    private fb: FormBuilder,
    private dropdownHttpService: DropdownHttpService,
    public blacklistTemplateService: BlacklistTemplateService,
    private dialog: MatDialog,
    private appPermissionService: AppPermissionService,
  ) {}

  /** True for both promotion- and rebate-side template selection modes. */
  get isTemplateSelectionMode(): boolean {
    return this.data?.mode === 'promotion-template' || this.data?.mode === 'rebate-template';
  }

  ngOnInit(): void {
    let action = this.data.mode;
    this.titleAction = action.charAt(0).toUpperCase() + action.slice(1);

    if (this.isTemplateSelectionMode) {
      this.selectedBlacklistId = this.data?.selected_blacklist_id ?? 0;

      this.form = this.fb.group({
        selected_blacklist_id: [this.selectedBlacklistId ?? 0],
        categories: this.fb.array([]),
      });

      this.loadTemplateDropdown();
    } else {
      this.form = this.fb.group({
        name: [this.data?.template?.name ?? '', [Validators.required]],
        status: [this.data?.template?.status ?? this.status.Active, [Validators.required]],
        remarks: [this.data?.template?.remarks ?? ''],
        categories: this.fb.array([]),
      });
    }

    // "Update Game Provider Blacklist" permission controls whether the Template
    // dropdown is editable. Different permission key per host module.
    const pSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      if (this.data.mode === 'rebate-template') {
        this.canEditBlacklist = !!appPermissions.rebate_settings_update_game_provider_blacklist;
      } else if (this.data.mode === 'promotion-template') {
        this.canEditBlacklist = !!appPermissions.edit_game_provider_blacklist;
      } else {
        this.canEditBlacklist = true;
      }

      this.templateDropdownSettings = {
        ...this.templateDropdownSettings,
        disabled: !this.canEditBlacklist,
      } as any;
    });
    this.sub.add(pSub);

    // Prepare currency
    const cSub = this.dropdownHttpService.currencies
      .pipe(
        tap((res) => {
          this.dropdown.currencies = res;
          this.selectedCurrency = res[0];
          this.loadInitialSetting();
        })
      )
      .subscribe();

    this.sub.add(cSub);

    this.sub.add(
      this.blacklistTemplateService.icon$.subscribe(icon => {
        if (icon) this.lastIcon = icon as any;
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get isReadOnly(): boolean {
    if (this.data?.mode === 'view') return true;
    if (this.isTemplateSelectionMode) {
      // Per spec, the "Update Game Provider Blacklist" permission only affects
      // the Template field's disabled state — not the whole config grid.
      return this.templateLocked;
    }
    return false;
  }

  get categoriesArray(): FormArray {
    return this.form.get('categories') as FormArray;
  }

  loadInitialSetting(): void {
    this.dataLoading = true;

    // -----------------------------------
    // Promotion / Rebate blacklist mode
    // -----------------------------------
    if (this.isTemplateSelectionMode) {
      this.buildFromRows((this.data.setting || {}) as unknown as RowsByCurrency);

      if (this.data.selected_sub_categories
    && Object.keys(this.data.selected_sub_categories).length > 0) {
  this.applySelectedSubCategories(this.data.selected_sub_categories);
}

      setTimeout(() => {
        if (this.selectedBlacklistId) {
          const matched = this.templateDropdownList.find(t => +t.id === +this.selectedBlacklistId);

          if (matched) {
            this.templateSelectedItems = [matched];
            this.templateLocked = true;

            this.form.get('selected_blacklist_id')?.setValue(matched.id, { emitEvent: false });
          }
        }
      });

      return;
    }

    // -----------------------------------
    // Create template = GLOBAL full config
    // -----------------------------------
    if (this.data.mode === 'create') {
      const cSub = this.blacklistTemplateService.getGameProvider().subscribe({
        next: (res) => {
          this.buildFromRows((res || {}) as unknown as RowsByCurrency);
        },
        error: () => {
          this.buildFromRows({});
        }
      });
      this.sub.add(cSub);
      return;
    }

    // -----------------------------------
    // Edit / Duplicate template
    // -----------------------------------
    if ((this.data.mode === 'edit' || this.data.mode === 'duplicate') && this.data?.template?.id) {
      const eSub = this.blacklistTemplateService.getGameProviderById(this.data.template.id).subscribe({
        next: (res) => {
          this.buildFromRows((res || {}) as unknown as RowsByCurrency);
        },
        error: () => {
          this.buildFromRows({});
        }
      });
      this.sub.add(eSub);
      return;
    }

    // fallback
    this.buildFromRows(this.data.setting || {});
  }

  private loadTemplateDropdown(): void {
    const tSub = this.blacklistTemplateService.getList('?paginate=false').subscribe({
      next: (res) => {
        this.templateDropdownList = res || [];

        if (this.selectedBlacklistId) {
          const matched = this.templateDropdownList.find(t => +t.id === +this.selectedBlacklistId);

          if (matched) {
            this.templateSelectedItems = [matched];
            this.selectedBlacklistId = matched.id;

            // restore dropdown value
            this.form.get('selected_blacklist_id')?.setValue(matched.id, { emitEvent: false });

            this.templateLocked = true;

            return;
          }
        }

        this.templateSelectedItems = [];
        this.templateLocked = false;
      },
      error: () => {
        this.templateDropdownList = [];
        this.templateSelectedItems = [];
        this.templateLocked = false;
      }
    });

    this.sub.add(tSub);
  }

  onTemplateDropdownChange(items: any[]): void {
    const selected = items?.length ? items[0] : null;

    this.templateSelectedItems = selected ? [selected] : [];
    this.selectedBlacklistId = +(selected?.id ?? 0);

    // Deselect template = keep current ticks, just unlock editing
    if (!this.selectedBlacklistId) {
      this.templateLocked = false;
      this.templateSelectedItems = [];
      return;
    }

    const gSub = this.blacklistTemplateService.getGameProviderById(this.selectedBlacklistId).subscribe({
      next: (res) => {
        this.templateLocked = true;
        this.buildFromSelectedCategories(res as unknown as RowsByCurrency);

        this.form.get('selected_blacklist_id')?.setValue(this.selectedBlacklistId, { emitEvent: false });

        this.templateSelectedItems = this.templateDropdownList.filter(
          t => t.id === this.selectedBlacklistId
        );
      },
      error: () => {
        this.templateLocked = false;
        this.selectedBlacklistId = 0;
        this.templateSelectedItems = [];
      }
    });

    this.sub.add(gSub);
  }

  // -------------------------
  // Build from selected categories shape
  // -------------------------
  private buildFromSelectedCategories(rowsByCurrency: RowsByCurrency): void {
    const controls = this.categoriesArray.controls as FormGroup[];

    controls.forEach((group) => {
      const currencyId = group.value.settings_currency_id;
      const providerId = group.value.game_provider_id;
      const categoryCode = group.value.category_code;

      const provider = (rowsByCurrency?.[currencyId] || []).find(
        (p: any) => p.game_provider_id === providerId
      );

      const category = provider?.categories?.find(
        (c: any) => c.code === categoryCode
      );

      const subArray = group.get('sub_categories') as FormArray;

      subArray.controls.forEach((subCtrl) => {
        const templateSub = category?.sub_categories?.find(
          (sc: any) => sc.id === subCtrl.value.id
        );

        subCtrl.get('status')?.setValue(templateSub?.status === 1, { emitEvent: false });
      });
    });

    this.getFilteredForm(this.selectedGameProvider[this.selectedIndex]?.id ?? 0);
  }

  // -------------------------
  // Build from API shape
  // -------------------------
  private buildFromRows(rowsByCurrency: RowsByCurrency) {
    this.dataLoading = true;
    this.formReady = false;

    this.categoryMap = {};
    this.categoriesArray.clear();

    if (!rowsByCurrency || Object.keys(rowsByCurrency).length === 0) {
      // Render empty state (e.g. no providers selected yet) instead of getting stuck on "Loading...".
      this.providersByCurrency = this.dropdown.currencies.map(() => []);
      this.unFilterProvidersByCurrency = this.dropdown.currencies.map(() => []);
      this.selectedGameProvider = this.dropdown.currencies.map(() => null);
      this.filteredFormWithIndex = [];
      this.formReady = true;
      this.dataLoading = false;
      return;
    }

    this.providersByCurrency = this.dropdown.currencies.map(() => []);
    this.unFilterProvidersByCurrency = this.dropdown.currencies.map(() => []);
    this.selectedGameProvider = this.dropdown.currencies.map(() => null);

    Object.entries(rowsByCurrency || {}).forEach(([currencyIdStr, providerRows]) => {
      const currencyId = Number(currencyIdStr);
      const tabIndex = this.dropdown.currencies.findIndex(c => c.id === currencyId);
      if (tabIndex < 0) return;

      this.categoryMap[currencyId] = {};
      const providerList: any[] = [];

      (providerRows || []).forEach((p: any) => {
        const provider = {
          id: p.game_provider_id,
          code: p.game_provider_code,
          name: p.game_provider_code,
        };
        providerList.push(provider);

        this.categoryMap[currencyId][p.game_provider_id] = {};

        // categories is an array
        (p.categories || []).forEach((cat: any) => {
          const fg = this.fb.group({
            game_provider_id: [p.game_provider_id],
            game_provider_code: [p.game_provider_code],

            // category
            category_id: [cat.id],
            category_code: [cat.code],
            category_name: [cat.name],

            settings_currency_id: [currencyId],
            expanded: [false],

            // sub_categories is inside each category
            sub_categories: this.fb.array(
              (cat.sub_categories || []).map((sc: any) =>
                this.fb.group({
                  id: [sc.id],
                  name: [sc.name],
                  status: [sc.status === 1], // boolean for mat-checkbox
                })
              )
            ),
          });

          this.categoriesArray.push(fg);
          this.categoryMap[currencyId][p.game_provider_id][cat.code] = fg;
        });
      });

      const uniq = Array.from(new Map(providerList.map(x => [x.id, x])).values());
      this.providersByCurrency[tabIndex] = uniq;
      this.unFilterProvidersByCurrency[tabIndex] = [...uniq];
      this.selectedGameProvider[tabIndex] = uniq[0] ?? null;
    });

    this.formReady = true;
    this.dataLoading = false;

    const firstProviderId = this.selectedGameProvider[0]?.id ?? 0;
    this.getFilteredForm(firstProviderId);

    if (this.isReadOnly) {
      this.form.disable({ emitEvent: false });
    }
  }

  // -------------------------
  // Currency / Provider
  // -------------------------
  setCurrency(event: any) {
    this.selectedIndex = event.index;
    this.selectedCurrency = this.dropdown.currencies[this.selectedIndex];

    // reset apply UI when switching tab
    this.selectedAppliedCurrencies = [];
    this.showCurrencyDialog = false;
    this.setAppliedState(false);

    // reset provider search list
    this.keyword = '';
    this.providersByCurrency[this.selectedIndex] = [...(this.unFilterProvidersByCurrency[this.selectedIndex] || [])];

    const providerId = this.selectedGameProvider[this.selectedIndex]?.id ?? 0;
    this.getFilteredForm(providerId);
  }

  setProvider(providerId: number) {
    const p = this.providersByCurrency[this.selectedIndex]?.find(x => x.id === providerId);
    this.selectedGameProvider[this.selectedIndex] = p ?? null;
    this.getFilteredForm(providerId);
  }

  private getFilteredForm(providerId: number) {
    const controls = this.categoriesArray.controls as FormGroup[];
    this.filteredFormWithIndex = controls
      .map((group, idx) => ({ idx, group }))
      .filter(x =>
        x.group.value.game_provider_id === providerId &&
        x.group.value.settings_currency_id === this.selectedCurrency.id
      );
  }

  private fg(providerId: number, categoryCode: string): FormGroup | null {
    return this.categoryMap[this.selectedCurrency.id]?.[providerId]?.[categoryCode] ?? null;
  }

  // -------------------------
  // Search provider
  // -------------------------
  onSearch(event?: any) {
    this.keyword = event ? (event.target.value || '') : (this.keyword || '');
    const kw = this.keyword.trim().toUpperCase();
    if (!kw) return;

    const list = this.unFilterProvidersByCurrency[this.selectedIndex] || [];
    this.providersByCurrency[this.selectedIndex] = list.filter(p =>
      (p.code || '').toUpperCase().includes(kw) || (p.name || '').toUpperCase().includes(kw)
    );
  }

  resetKeyword(event: any) {
    if (!event?.target?.value) {
      this.keyword = '';
      this.providersByCurrency[this.selectedIndex] = [...(this.unFilterProvidersByCurrency[this.selectedIndex] || [])];
    }
  }

  // -------------------------
  // Category expand
  // -------------------------
  toggleDropdown(providerId: number, code: string) {
    const g = this.fg(providerId, code);
    g?.get('expanded')?.setValue(!g.get('expanded')?.value);
  }

  // -------------------------
  // Main category checkbox behavior
  // (sub checkbox are formControlName="status" already)
  // -------------------------
  toggleMain(e: any, providerId: number, code: string) {
    if (this.isReadOnly) return;

    const g = this.fg(providerId, code);
    if (!g) return;

    const checked = !!e.checked;
    const arr = g.get('sub_categories') as FormArray;
    arr.controls.forEach(c => c.get('status')?.setValue(checked));
  }

  isMainChecked(providerId: number, code: string): boolean {
    const g = this.fg(providerId, code);
    const arr = g?.get('sub_categories') as FormArray;
    return !!arr && arr.length > 0 && arr.controls.every(c => c.get('status')?.value === true);
  }

  isMainIndeterminate(providerId: number, code: string): boolean {
    const g = this.fg(providerId, code);
    const arr = g?.get('sub_categories') as FormArray;
    if (!arr || arr.length === 0) return false;

    const checked = arr.controls.filter(c => c.get('status')?.value === true).length;
    return checked > 0 && checked < arr.length;
  }

  // -------------------------
  // Apply to other currencies
  // -------------------------
  toggleCurrencyDialog() {
    this.showCurrencyDialog = !this.showCurrencyDialog;
    this.setAppliedState(false);
  }

  otherCurrencies() {
    return (this.dropdown.currencies || []).filter(c =>
      c.id !== this.selectedCurrency.id &&
      !!this.categoryMap[c.id] // only currencies that have data loaded in form
    );
  }

  toggleAllCurrencies(event: any) {
    this.selectedAppliedCurrencies = event.target.checked ? this.otherCurrencies().map(c => c.id) : [];
  }

  toggleCurrency(currencyId: number) {
    if (this.selectedAppliedCurrencies.includes(currencyId)) {
      this.selectedAppliedCurrencies = this.selectedAppliedCurrencies.filter(id => id !== currencyId);
    } else {
      this.selectedAppliedCurrencies.push(currencyId);
    }
  }

  isAllOtherCurrenciesChecked() {
    return this.selectedAppliedCurrencies.length === this.otherCurrencies().length && this.otherCurrencies().length > 0;
  }

  applyToOtherCurrencies() {
    const sourceCurrencyId = this.selectedCurrency.id;

    this.selectedAppliedCurrencies.forEach(targetCurrencyId => {
      const sourceByProvider = this.categoryMap[sourceCurrencyId];
      const targetByProvider = this.categoryMap[targetCurrencyId];
      if (!sourceByProvider || !targetByProvider) return;

      Object.keys(sourceByProvider).forEach(providerIdStr => {
        const providerId = Number(providerIdStr);
        const sourceCats = sourceByProvider[providerId] || {};
        const targetCats = targetByProvider[providerId] || {};

        Object.keys(sourceCats).forEach(categoryCode => {
          const sourceFG = sourceCats[categoryCode];
          const targetFG = targetCats[categoryCode];
          if (!sourceFG || !targetFG) return;

          const sourceArr = sourceFG.get('sub_categories') as FormArray;
          const targetArr = targetFG.get('sub_categories') as FormArray;

          const sourceStatusById = new Map<number, boolean>();
          sourceArr.controls.forEach(ctrl => {
            sourceStatusById.set(Number(ctrl.get('id')?.value), !!ctrl.get('status')?.value);
          });

          targetArr.controls.forEach(ctrl => {
            const id = Number(ctrl.get('id')?.value);
            if (sourceStatusById.has(id)) {
              ctrl.get('status')?.setValue(sourceStatusById.get(id)!, { emitEvent: false });
            }
          });
        });
      });
    });

    this.setAppliedState(true);

    setTimeout(() => {
      this.setAppliedState(false);
      this.showCurrencyDialog = false;
      this.selectedAppliedCurrencies = [];
    }, 1000);
  }

  private setAppliedState(applied: boolean) {
    this.beforeApplied = !applied;
    this.afterApplied = applied;
  }

  // -------------------------
  // Close / Save
  // -------------------------
  onCloseDialog() {
    this.dialogRef.close();
  }

  onAlertConfirmed() {
    const wasError = this.lastIcon === 'error';

    // clear swal message AFTER capturing wasError
    this.blacklistTemplateService.clearMessages();

    if (wasError) return;   // keep modal open
    this.onCloseDialog();   // success close
  }

  onSave(): void {
    if (!this.form) return;

    if (this.isTemplateSelectionMode) {
      const payload = this.buildSubmitPayload();

      this.dialogRef.close({
        blacklist_id: payload.blacklist_id ?? null,
        sub_categories: payload.sub_categories ?? {},
        blacklist_sub_categories: payload.blacklist_sub_categories ?? [],
      });
      return;
    }

    // existing template create / update logic below
    const payload = {
      name: this.form.value.name,
      remarks: this.form.value.remarks,
      status: this.form.value.status,
      sub_categories: this.buildSubmitPayload().sub_categories,
    };

    this.saving = true;

    const req$ = this.data.mode === 'edit'
      ? this.blacklistTemplateService.updateTemplate(this.data.template.id, payload)
      : this.blacklistTemplateService.createTemplate(payload);

    req$.pipe(
      finalize(() => (this.saving = false))
    ).subscribe({
      next: () => {},
      error: () => {}
    });
  }

  // True if this provider has ANY checked sub-category in the selected currency
  hasAnyChecked(providerId: number): boolean {
    const currencyId = this.selectedCurrency?.id;
    if (!currencyId) return false;

    const catsByCode = this.categoryMap?.[currencyId]?.[providerId];
    if (!catsByCode) return false;

    return Object.values(catsByCode).some((fg: FormGroup) => {
      const arr = fg.get('sub_categories') as FormArray | null;
      if (!arr) return false;
      return arr.controls.some(sc => sc.get('status')?.value === true);
    });
  }

  openBlacklistTemplateDialog(): void {
    const dialogRef = this.dialog.open(BlacklistTemplateSettingComponent, {
      width: '1200px',
      data: {
        mode: 'create',
        template: null,
        setting: null, // IMPORTANT: don't pass promotion-limited rows
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadTemplateDropdown();
    });
  }

  private buildSubmitPayload(): {
    blacklist_id: number | null;
    sub_categories: any;
    blacklist_sub_categories: any[];
  } {
    const controls = this.categoriesArray.controls as FormGroup[];
    const blacklist_sub_categories: any[] = [];

    // ---------------------------------------
    // MODE 1: promotion-template / rebate-template
    // Needs reopen state by currency + provider
    // ---------------------------------------
    if (this.isTemplateSelectionMode) {
      const sub_categories: Record<number, Record<number, number[]>> = {};

      controls.forEach((group) => {
        const currencyId = +group.get('settings_currency_id')?.value;
        const providerId = +group.get('game_provider_id')?.value;
        const categoryId = +group.get('category_id')?.value;
        const categoryCode = group.get('category_code')?.value;

        const subArray = group.get('sub_categories') as FormArray;

        subArray.controls.forEach((subCtrl) => {
          const checked = !!subCtrl.get('status')?.value;
          const subCategoryId = +subCtrl.get('id')?.value;
          const subCategoryName = subCtrl.get('name')?.value;

          if (!checked) return;

          // --------------------------
          // UI reopen state
          // --------------------------
          if (!sub_categories[currencyId]) {
            sub_categories[currencyId] = {};
          }

          if (!sub_categories[currencyId][providerId]) {
            sub_categories[currencyId][providerId] = [];
          }

          if (!sub_categories[currencyId][providerId].includes(subCategoryId)) {
            sub_categories[currencyId][providerId].push(subCategoryId);
          }

          // --------------------------
          // API payload for promotion
          // --------------------------
          blacklist_sub_categories.push({
            settings_currency_id: currencyId,
            game_provider_id: providerId,
            category_id: categoryId,
            category_code: categoryCode,
            sub_category_id: subCategoryId,
            sub_category_name: subCategoryName,
          });
        });
      });

      return {
        blacklist_id: this.selectedBlacklistId ?? null,
        sub_categories,
        blacklist_sub_categories,
      };
    }

    // ---------------------------------------
    // MODE 2: template CRUD (keep old behavior)
    // currencyId -> subCategoryIds[]
    // ---------------------------------------
    const sub_categories: Record<number, number[]> = {};

    controls.forEach((group) => {
      const currencyId = +group.get('settings_currency_id')?.value;
      const categoryId = +group.get('category_id')?.value;
      const categoryCode = group.get('category_code')?.value;
      const providerId = +group.get('game_provider_id')?.value;

      const subArray = group.get('sub_categories') as FormArray;

      subArray.controls.forEach((subCtrl) => {
        const checked = !!subCtrl.get('status')?.value;
        const subCategoryId = +subCtrl.get('id')?.value;
        const subCategoryName = subCtrl.get('name')?.value;

        if (!checked) return;

        if (!sub_categories[currencyId]) {
          sub_categories[currencyId] = [];
        }

        if (!sub_categories[currencyId].includes(subCategoryId)) {
          sub_categories[currencyId].push(subCategoryId);
        }

        blacklist_sub_categories.push({
          settings_currency_id: currencyId,
          game_provider_id: providerId,
          category_id: categoryId,
          category_code: categoryCode,
          sub_category_id: subCategoryId,
          sub_category_name: subCategoryName,
        });
      });
    });

    return {
      blacklist_id: this.selectedBlacklistId ?? null,
      sub_categories,
      blacklist_sub_categories,
    };
  }

  private applySelectedSubCategories(
    saved: Record<number, Record<number, number[]>> = {}
  ): void {
    const controls = this.categoriesArray.controls as FormGroup[];

    controls.forEach((group) => {
      const currencyId = +group.get('settings_currency_id')?.value;
      const providerId = +group.get('game_provider_id')?.value;

      const selectedIds = saved?.[currencyId]?.[providerId] ?? [];

      const subArray = group.get('sub_categories') as FormArray;

      subArray.controls.forEach((subCtrl) => {
        const subId = +subCtrl.get('id')?.value;
        const shouldCheck = selectedIds.includes(subId);

        subCtrl.get('status')?.setValue(shouldCheck, { emitEvent: false });
      });
    });

    this.getFilteredForm(this.selectedGameProvider[this.selectedIndex]?.id ?? 0);
  }
}
